import React from "react";
import {
  Mail, XCircle, AlertTriangle, Send, CheckCircle2,
} from "lucide-react";

/** Email/SMTP diagnostics modal — extracted from Admin.jsx for clarity.
 *  All side effects (fetch, send, force-reset) are owned by the parent;
 *  this component is a pure presentational shell that wires the data and
 *  callbacks. Keeps Admin.jsx under 500 lines. */
const EmailDiagnosticsModal = ({
  open, onClose,
  diag, loading,
  testTo, setTestTo,
  testResult,
  onSendTest, onForceReset,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
         onClick={onClose}
         data-testid="email-diag-backdrop">
      <div className="bg-[#161920] border border-white/10 rounded-2xl p-6 md:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}
           data-testid="email-diag-modal">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <span className="label-uppercase inline-flex items-center gap-2">
              <Mail className="w-3.5 h-3.5 text-[#C5A880]" /> SMTP
            </span>
            <h3 className="font-display text-2xl mt-2 text-[#F5F2EE]">Diagnóstico de email</h3>
          </div>
          <button onClick={onClose}
                  className="text-[#A29F9B] hover:text-[#F5F2EE]"
                  data-testid="email-diag-close">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        {loading && !diag ? (
          <p className="text-sm text-[#A29F9B]">A carregar…</p>
        ) : diag ? (
          <>
            {/* SMTP config snapshot */}
            <div className="bg-[#0C0E12] border border-white/5 rounded-xl p-4 mb-5">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div className="text-[#A29F9B]">Configurado</div>
                <div className={diag.smtp_config.configured ? "text-emerald-300" : "text-rose-300"}>
                  {diag.smtp_config.configured ? "Sim" : "Não (emails em modo DRY-RUN)"}
                </div>
                <div className="text-[#A29F9B]">Host</div>
                <div className="text-[#F5F2EE] font-mono">{diag.smtp_config.host || "—"}:{diag.smtp_config.port}</div>
                <div className="text-[#A29F9B]">User</div>
                <div className="text-[#F5F2EE] font-mono">{diag.smtp_config.user || "—"}</div>
                <div className="text-[#A29F9B]">Password</div>
                <div className={diag.smtp_config.password_set ? "text-emerald-300" : "text-rose-300"}>
                  {diag.smtp_config.password_set ? "Definida" : "EM FALTA"}
                </div>
                <div className="text-[#A29F9B]">From</div>
                <div className="text-[#F5F2EE]">{diag.smtp_config.from_name} &lt;{diag.smtp_config.from}&gt;</div>
                <div className="text-[#A29F9B]">TLS</div>
                <div className="text-[#F5F2EE]">{diag.smtp_config.use_tls ? "STARTTLS" : "SSL (implicit)"}</div>
              </div>
            </div>

            {/* Last error */}
            {diag.last_smtp_error?.error && (
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 mb-5"
                   data-testid="email-diag-last-error">
                <p className="text-xs text-rose-300 uppercase tracking-widest mb-1 inline-flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" /> Último erro SMTP
                </p>
                <p className="text-sm font-mono text-[#F5F2EE]">{diag.last_smtp_error.error}</p>
                <p className="text-xs text-[#A29F9B] mt-1">
                  para <b>{diag.last_smtp_error.to}</b> em {diag.last_smtp_error.at?.slice(0, 19).replace("T", " ")}
                </p>
              </div>
            )}

            {/* Test send */}
            <div className="border border-white/5 rounded-xl p-4 mb-5">
              <p className="text-xs text-[#C5A880] uppercase tracking-widest mb-3 inline-flex items-center gap-1.5">
                <Send className="w-3 h-3" /> Enviar email de teste
              </p>
              <div className="flex gap-2">
                <input type="email" value={testTo}
                       onChange={(e) => setTestTo(e.target.value)}
                       placeholder="email-para-testar@exemplo.com"
                       className="flex-1 bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm"
                       data-testid="email-diag-to" />
                <button onClick={onSendTest} disabled={loading}
                        className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                        data-testid="email-diag-send">
                  <Send className="w-3.5 h-3.5" />
                  {loading ? "…" : "Teste SMTP"}
                </button>
                <button onClick={onForceReset} disabled={loading}
                        className="text-sm inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#C5A880]/40 bg-[#C5A880]/10 text-[#C5A880] hover:bg-[#C5A880]/20 disabled:opacity-50"
                        title="Gera um novo token de 60min e envia email de recuperação (bypass rate-limit)."
                        data-testid="email-diag-force-reset">
                  Forçar recuperação
                </button>
              </div>
              {testResult && (
                <div className={`mt-3 p-2 rounded-lg text-xs ${testResult.ok ? "bg-emerald-500/10 text-emerald-300" : "bg-rose-500/10 text-rose-300"}`}>
                  {testResult.ok ? (
                    <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3" /> {testResult.label || `Entregue ao SMTP para ${testResult.target}.`}</span>
                  ) : (
                    <>
                      <div className="font-semibold mb-1">Falhou.</div>
                      <div className="font-mono">{testResult.last_smtp_error?.error || testResult.detail || "Sem detalhe."}</div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Recent sends */}
            <div>
              <p className="text-xs text-[#C5A880] uppercase tracking-widest mb-3">Últimos envios</p>
              {(diag.recent_sends || []).length === 0 ? (
                <p className="text-xs text-[#A29F9B] italic">Sem histórico.</p>
              ) : (
                <ul className="space-y-2">
                  {diag.recent_sends.map((s, i) => (
                    <li key={s.created_at + "-" + i}
                        className="flex items-center justify-between gap-3 text-xs bg-[#0C0E12] border border-white/5 rounded-lg p-2">
                      <span className={s.ok ? "text-emerald-300" : "text-rose-300"}>
                        {s.ok ? "✓" : "✗"}
                      </span>
                      <span className="flex-1 truncate text-[#DCD7D2]">{s.subject}</span>
                      <span className="text-[#A29F9B] font-mono">{s.to}</span>
                      <span className="text-[#A29F9B]/70 whitespace-nowrap">
                        {s.created_at?.slice(11, 19)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default EmailDiagnosticsModal;
